import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ListItem, Virtuoso, VirtuosoProps, VirtuosoHandle } from 'react-virtuoso';
import classNames from 'classnames';

import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import useTrackScrollPosition from '@/utils/use-track-scroll-position';
import useClickAwayListener from '@/utils/use-click-away-listener';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { mergeRefs } from '@/utils/merge-refs';
import useTrackResize from '@/utils/use-track-resize';
import useScrollToIndex from './use-scroll-to-index';
import { TCustomCarouselProps } from './custom-carousel.types';

import styles from './custom-carousel.module.css';
import { useTrackScrollEnd } from '@/utils/use-track-scrollend';

const CustomCarousel = <T,>({
  onPointerDownInside,
  onPointerUpWithDrag,
  onPointerUpWithoutDrag,
  list = [],
  renderItem,
  gap = '8px',
  showArrows = true,
  virtualized = false,
  scrollRef,
  allowDragOnClickableItems = true,
  justify,
  forceJustifyCenter,
  itemContainerDimensions,
  scrollTo,
  customComponents = {},
  onScrollEnd,
  ignorePointerEvents = true,
}: TCustomCarouselProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const itemsHaveRenderedRef = useRef(false);
  const firstItemRendered = useRef(false);
  const lastItemRendered = useRef(false);
  const onScrollEndRef = useRef(onScrollEnd);
  const blockDragging = useRef(!allowDragOnClickableItems);

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [itemsHaveRendered, setItemsHaveRendered] = useState(false);
  // Keeping this around but not yet implementing because it's a little buggy when scrolling fast
  // it's intention is to programatically smooth scroll to the nearest item on mouse release
  // const [scrollToOffset, setScrollToOffset] = useState<number | null>(null);

  useScrollToIndex({
    virtuosoRef,
    scrollContainerRef,
    scrollTo,
    firstItemRendered: firstItemRendered.current,
    lastItemRendered: lastItemRendered.current,
    virtualized,
    gap,
  });

  useEffect(() => {
    onScrollEndRef.current = onScrollEnd;
  }, [onScrollEnd]);

  const scrolled = useTrackScrollEnd({ ref: scrollContainerRef });

  useEffect(() => {
    onScrollEndRef.current?.(scrolled);
  }, [scrolled]);

  const trackedScrollDimensions = useTrackResize({
    ref: scrollContainerRef,
    trackType: 'debounced',
    debounceTime: 500,
  });

  const trackedScrollWidth = trackedScrollDimensions.width || 1;

  const scrollPos = useTrackScrollPosition({
    containerRef: scrollContainerRef,
    debounceTime: 0,
    rerunCounter: itemsHaveRendered || !virtualized ? trackedScrollWidth : 0,
  });

  useClickAwayListener({
    open: !!scrollContainerRef.current,
    ref: scrollContainerRef,
    onPointerDownInside: (e) => {
      if (ignorePointerEvents) {
        return;
      }

      setIsMouseDown(true);
      setHasDragged(false);
      onPointerDownInside?.(e);
      if (!allowDragOnClickableItems) {
        const clickableTags = [
          'a[href]',
          'area[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          'button:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
          '[contenteditable]',
        ];
        const target = e.target as HTMLElement;
        const isClickableItem = clickableTags.some((tag) => target.closest(tag));
        if (isClickableItem) {
          blockDragging.current = true;
        } else {
          blockDragging.current = false;
        }
      }
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grabbing';
      }
    },
    onPointerUpInside: (e) => {
      if (ignorePointerEvents) {
        return;
      }

      setIsMouseDown(false);
      blockDragging.current = false;
      if (hasDragged) {
        onPointerUpWithDrag?.(e);
      } else if (!hasDragged) {
        onPointerUpWithoutDrag?.(e);
      }
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
      }
      setHasDragged(false);
    },
    onPointerUpOutside: () => {
      if (ignorePointerEvents) {
        return;
      }

      setIsMouseDown(false);
      setHasDragged(false);
      blockDragging.current = false;
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
      }
    },
  });

  // drag to scroll
  useEffect(() => {
    if (ignorePointerEvents) {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      if ((!blockDragging.current && isMouseDown && e.movementX > 1) || e.movementX < -1) {
        container.style.cursor = 'grabbing';
        container.scrollLeft -= e.movementX;
        setHasDragged(true);
      }
    };

    // on drag, scroll
    if (isMouseDown && !blockDragging.current) {
      document.addEventListener('mousemove', handleMouseMove);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isMouseDown, scrollContainerRef, blockDragging, ignorePointerEvents]);

  const VirtualizedWrapper = virtualized ? Virtuoso : Fragment;
  const virtualizedProps: VirtuosoProps<T & { id: string | number }, unknown> = useMemo(() => {
    if (!virtualized) return {};
    return {
      data: list,
      style: { width: '100%', height: '100%' },
      ref: virtuosoRef,
      scrollerRef: mergeRefs(scrollRef, scrollContainerRef),
      components: customComponents,
      tabIndex: -1,
      itemsRendered: (items: ListItem<unknown>[]) => {
        if (!items || !items.length) return;
        // scroll snap doesn't work with virtuoso
        // so programmatically handling it
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        // Force a scroll tracking re-render becasuse virtuoso doesn't render all items in one go
        // it incremtally adds items to the DOM, which means the first render won't contain all items
        // and the scroll position will be incorrect.
        // Also, using a ref to track state instead of the useState hook because I don't want this
        // function to unecessarily re-render on that state change.
        if (!itemsHaveRenderedRef.current) {
          let allItemsSize = items.reduce((acc, item) => acc + item.size, 0);
          const gapAdjust = gap ? parseInt(gap.toString()) * (items.length - 1) : 0;
          allItemsSize += gapAdjust;
          if (allItemsSize > scrollContainer.clientWidth) {
            setItemsHaveRendered(true);
            itemsHaveRenderedRef.current = true;
          }
        }

        // Keeping this around but not yet implementing because it's a little buggy when scrolling fast
        // it's intention is to programatically smooth scroll to the nearest item on mouse release
        // setScrollToOffset(items?.[0].offset);
        // if (items.length === 1) {
        //   setScrollToOffset(items?.[0].offset);
        //   return;
        // }
        // if first item is more than 50% visible, scroll to first item
        // else scroll to second item
        // const scrollLeft = scrollContainer.scrollLeft;
        // const firstItem = items[0];
        // const secondItem = items[1];
        // const firstItemSize = firstItem.size;

        // const firstItemOffset = firstItem.offset;
        // const firstItemMidpointPosition = firstItemOffset + firstItemSize / 2;
        // if (scrollLeft > firstItemMidpointPosition) {
        //   setScrollToOffset(secondItem.offset);
        // } else {
        //   setScrollToOffset(firstItem.offset);
        // }
      },
      // Keeping this around but not yet implementing because it's a little buggy when scrolling fast
      // it's intention is to programatically smooth scroll to the nearest item on mouse release
      // isScrolling: (isScrolling) => {
      //   const el = scrollContainerRef.current;
      //   if (!el) return;
      //   // force scroll to neareset item (css scroll snap doesn't work with virtuoso)
      //   if (!isScrolling && !isMouseDown) {
      //     if (scrollToOffset === null) return;
      //     setScrollToOffset(null);
      //     el.scrollTo({
      //       left: scrollToOffset,
      //       behavior: 'smooth',
      //     });
      //   }
      // },
      className: `no-scrollbar theme-pack-carousel ${styles['custom-carousel'] || ''} ${styles['virtualized'] || ''} ${justify === 'center' ? styles['justify-center'] : ''}`,
      horizontalDirection: true,
      itemContent: (index, item) => {
        const _item = item as T & { id: string | number };
        const isLastItem = index === list.length - 1;
        const isFirstItem = index === 0;
        return (
          <Container
            key={_item.id}
            id={`custom-carousel-item-${index}`}
            data-index={index}
            className={`theme-pack-carousel-item ${styles['custom-carousel-item'] || ''}`}
            width="100%"
            height="100%"
            ref={() => {
              if (isFirstItem) {
                firstItemRendered.current = true;
              }
              if (isLastItem) {
                lastItemRendered.current = true;
              }
            }}
            style={{
              ...(!isLastItem && gap ? { marginRight: gap } : {}),
            }}
          >
            {renderItem(_item, index)}
          </Container>
        );
      },
    };
  }, [virtualized, list, scrollRef, customComponents, justify, gap, renderItem]);

  const ScrollContainer = virtualized ? Fragment : Container;
  const scrollContainerProps = useMemo(() => {
    if (virtualized) return {};
    return {
      ref: mergeRefs(scrollRef, scrollContainerRef),
      background: 'transparent',
      maxWidth: '100%',
      width: '100%',
      height: '100%',
      className: `flex no-scrollbar ${styles['custom-carousel-scroll-container'] || ''} ${justify === 'center' ? styles['justify-center'] : ''}  ${forceJustifyCenter ? styles.forceJustifyCenter : ''}`,
      gap: gap,
      style: {
        scrollSnapType: isMouseDown ? 'none' : 'x mandatory',
      },
    };
  }, [gap, isMouseDown, justify, scrollRef, virtualized, forceJustifyCenter]);

  return (
    <Container
      data-component="CustomCarousel"
      width="100%"
      height="100%"
      className={`position-relative flex ai-center`}
    >
      <OptionalComponent display={showArrows}>
        <Button
          className={`clear-button padding-8 radius-full ${styles['custom-carousel-button'] || ''} ${styles['left'] || ''}`}
          disabled={scrollPos.isLeft}
          style={{ ...(scrollPos.isLeft ? { opacity: 0 } : {}) }}
          backgroundColor="rgba(var(--gray-0-rgb), 0.05)"
          hoverBackgroundColor="rgba(var(--gray-0-rgb), 0.3)"
          border="1px solid rgba(var(--gray-true-dark-0-rgb), 0.05)"
          onClick={() => {
            const containerEl = scrollContainerRef.current;
            if (!containerEl) return;
            const scrollAmount = containerEl.clientWidth;
            /* there seems to be an additional 2px in virtuoso, maybe a border, so adjusting for that */
            const additionalGap = virtualized && gap ? parseInt(gap.toString()) - 2 : 0;
            containerEl.scrollTo({
              left: containerEl.scrollLeft - scrollAmount - additionalGap,
              behavior: 'smooth',
            });
          }}
        >
          <Icon name={ICONS.ARROW_LEFT_3} size={16} />
        </Button>
      </OptionalComponent>
      <Container
        width="100%"
        height="100%"
        className={classNames(`overflow-hidden flex ai-center`, {
          [styles.maskLeft]: !scrollPos.isLeft,
          [styles.maskRight]: !scrollPos.isRight,
          [styles.maskBoth]: !scrollPos.isLeft && !scrollPos.isRight,
        })}
      >
        <ScrollContainer {...scrollContainerProps}>
          <VirtualizedWrapper {...virtualizedProps}>
            {list.map((item, idx) => (
              <Container
                key={item.id}
                id={`custom-carousel-item-${idx}`}
                data-index={idx}
                ref={() => {
                  if (idx === 0) {
                    firstItemRendered.current = true;
                  }
                  if (idx === list.length - 1) {
                    lastItemRendered.current = true;
                  }
                }}
                className={`theme-pack-carousel-item ${styles['custom-carousel-item']}`}
                width={itemContainerDimensions?.width || '100%'}
                height={itemContainerDimensions?.height || '100%'}
              >
                {renderItem(item, idx)}
              </Container>
            ))}
          </VirtualizedWrapper>
        </ScrollContainer>
      </Container>
      <OptionalComponent display={showArrows}>
        <Button
          className={`clear-button padding-8 radius-full ${styles['custom-carousel-button'] || ''} ${styles['right'] || ''}`}
          backgroundColor="rgba(var(--gray-0-rgb), 0.05)"
          hoverBackgroundColor="rgba(var(--gray-0-rgb), 0.3)"
          border="1px solid rgba(var(--gray-true-dark-0-rgb), 0.05)"
          disabled={scrollPos.isRight}
          style={{ ...(scrollPos.isRight ? { opacity: 0 } : {}) }}
          onClick={() => {
            const containerEl = scrollContainerRef.current;
            if (!containerEl) return;

            const scrollAmount = containerEl.clientWidth;
            /* there seems to be an additional 2px in virtuoso, maybe a border, so adjusting for that */
            const additionalGap = virtualized && gap ? parseInt(gap.toString()) - 2 : 0;
            containerEl.scrollTo({
              left: containerEl.scrollLeft + scrollAmount + additionalGap,
              behavior: 'smooth',
            });
          }}
        >
          <Icon name={ICONS.ARROW_RIGHT_3} size={16} />
        </Button>
      </OptionalComponent>
    </Container>
  );
};

export default CustomCarousel;
